import { callEdgeFunction, EdgeFunctionError } from './edgeClient';

// ============================================
// STOCK TAKE FUNCTIONS
// ============================================

// Submit stock take data to Priority ERP via Edge Function
// docNo: optional IC document number - if provided, will PATCH existing document instead of creating new
// sessionId: optional session ID for server-side persistence tracking
// idempotencyKey: optional key identifying this submission attempt. Retrying the same
// submission with the same key returns the original result (flagged `duplicate: true`)
// instead of adjusting stock twice.
export const submitStockTake = async (items, docNo = null, sessionId = null, warehouse = 'FG', idempotencyKey = null) => {
  try {
    // Validate input
    if (!items || !Array.isArray(items) || items.length === 0) {
      throw new Error('No items provided for stock take');
    }

    // Validate each item
    for (const item of items) {
      if (!item.barcode || typeof item.barcode !== 'string') {
        throw new Error('Each item must have a valid barcode');
      }
      // Allow count of 0 (means no stock found during IC document scan)
      if (typeof item.count !== 'number' || item.count < 0) {
        throw new Error('Each item must have a count of 0 or more');
      }
    }

    const body = { items, docNo, sessionId, warehouse };
    if (idempotencyKey) body.idempotencyKey = idempotencyKey;

    let responseData;
    try {
      responseData = await callEdgeFunction('stock-take', { body });
    } catch (error) {
      if (!(error instanceof EdgeFunctionError)) throw error;
      // Return the full error response from edge function (includes Priority details)
      const errorData = error.body || {};
      console.error('Stock take submission failed:', errorData);
      return {
        success: false,
        // 409: the same idempotency key is still being processed by an earlier attempt
        isConflict: error.status === 409,
        message: errorData.error || `Stock take submission failed: ${error.status}`,
        priorityResponse: errorData.priorityResponse,
        priorityStatus: errorData.priorityStatus,
        adjustments: errorData.adjustments,
        currentStock: errorData.currentStock
      };
    }

    // A retried submission (same idempotencyKey) returns the original body plus
    // `duplicate: true`; it is handled exactly like the original success.
    const duplicate = responseData?.duplicate === true;
    const data = responseData;

    return {
      success: true,
      duplicate,
      message: data.message || 'Stock take submitted successfully to Priority ERP',
      response: data
    };
  } catch (error) {
    console.error('Error submitting stock take:', error);
    return {
      success: false,
      message: error.message || 'Failed to submit stock take',
      isNetworkError: true
    };
  }
};

// Get stock take history from Edge Function
export const getStockTakeHistory = async () => {
  try {
    const responseData = await callEdgeFunction('stock-take-history', { method: 'GET' });

    return {
      success: true,
      documents: responseData.documents || [],
      message: 'Stock take history retrieved successfully'
    };
  } catch (error) {
    console.error('Error fetching stock take history:', error);
    return {
      success: false,
      message: (error instanceof EdgeFunctionError && !error.body?.error)
        ? `Failed to fetch stock take history: ${error.status}`
        : (error.message || 'Failed to fetch stock take history'),
      documents: []
    };
  }
};

// Get IC document details from Priority ERP
export const getICDocument = async (docNo) => {
  try {
    if (!docNo || typeof docNo !== 'string') {
      throw new Error('Document number is required');
    }

    const responseData = await callEdgeFunction('get-ic-document', { body: { docNo } });

    return {
      success: true,
      docNo: responseData.docNo,
      curDate: responseData.curDate,
      warehouse: responseData.warehouse,
      location: responseData.location,
      items: responseData.items || [],
      sessionId: responseData.sessionId,  // Server-side session ID for persistence
      // Set when an active session already exists for this document. `sessionId` is
      // always a NEW session; to resume the existing one the caller abandons the new one.
      existingSessionId: responseData.existingSessionId || null,
      existingScannedItemCount: responseData.existingScannedItemCount || 0,
      message: 'IC document retrieved successfully'
    };
  } catch (error) {
    console.error('Error fetching IC document:', error);
    return {
      success: false,
      message: (error instanceof EdgeFunctionError && !error.body?.error)
        ? `Failed to fetch IC document: ${error.status}`
        : (error.message || 'Failed to fetch IC document'),
      items: []
    };
  }
};
